/**
 * app/fonts/FontProvider.tsx
 * ---------------------------
 * What: Ensures custom fonts are loaded before rendering the app tree.
 * Why: Prevents layout shift and ensures consistent typography across screens.
 */
import React from 'react';
import { Text, TextInput, Platform } from 'react-native';
import { useFonts, Inter_400Regular, Inter_500Medium, Inter_600SemiBold, Inter_700Bold } from '@expo-google-fonts/inter';
import * as SplashScreen from 'expo-splash-screen';

// Ensure the splash screen stays visible until fonts are loaded
// This runs at module load, before the component renders
try {
  void SplashScreen.preventAutoHideAsync();
} catch {}

/**
 * `FontProvider` ensures the Inter font family is loaded before the UI renders.
 * Once loaded it sets Inter as the default font for **all** <Text> and <TextInput>
 * components by mutating their defaultProps. This avoids passing fontFamily
 * manually throughout the app while still allowing individual overrides.
 */
export const FontProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [fontsLoaded] = useFonts({
    Inter_400Regular,
    Inter_500Medium,
    Inter_600SemiBold,
    Inter_700Bold,
  });

  // Fallback: allow rendering after a short timeout even if fonts fail to load (e.g., blocked network on web)
  const [readyToRender, setReadyToRender] = React.useState(false);
  React.useEffect(() => {
    if (fontsLoaded) {
      setReadyToRender(true);
      return;
    }
    const timeout = setTimeout(() => setReadyToRender(true), 5000);
    return () => clearTimeout(timeout);
  }, [fontsLoaded]);

  // Apply defaults synchronously once to avoid initial flash with system font
  const defaultsAppliedRef = React.useRef(false);
  if (readyToRender && fontsLoaded && !defaultsAppliedRef.current) {
    // Cast to any to avoid TypeScript complaining about readonly defaultProps
    const RNText = Text as any;
    const RNTextInput = TextInput as any;

    RNText.defaultProps = RNText.defaultProps || {};
    RNText.defaultProps.style = [{ fontFamily: 'Inter_400Regular' }, RNText.defaultProps.style];

    RNTextInput.defaultProps = RNTextInput.defaultProps || {};
    RNTextInput.defaultProps.style = [{ fontFamily: 'Inter_400Regular' }, RNTextInput.defaultProps.style];

    // ───── Web-only: ensure DOM inherits Inter ─────
    if (Platform.OS === 'web') {
      const root = document.getElementById('root');
      if (root) {
        root.style.fontFamily = 'Inter_400Regular, Inter, sans-serif';
      }

      // Map heavier fontWeight classes to real Inter weights for crisp rendering
      if (!document.getElementById('inter-weight-map')) {
        const map = document.createElement('style');
        map.id = 'inter-weight-map';
        map.innerHTML = `
          /* Semibold (fontWeight 600) */
          [class*="r-fontWeight-vw2c0b"], /* observed hash for 600 */
          [class*="r-fontWeight-5wxlh"],  /* fallback if hash changes */
          [style*="font-weight: 600"] {
            font-family: Inter_600SemiBold, Inter, sans-serif !important;
          }

          /* Bold (fontWeight 700) */
          [class*="r-fontWeight-maxjgm"], /* observed hash for 700 */
          [class*="r-fontWeight-1b9h3r"],
          [style*="font-weight: 700"] {
            font-family: Inter_700Bold, Inter, sans-serif !important;
          }
        `;
        document.head.appendChild(map);
      }
    }

    defaultsAppliedRef.current = true;
  }

  // Hide the splash screen once fonts are loaded (and defaults applied above)
  React.useEffect(() => {
    if (readyToRender) {
      try {
        void SplashScreen.hideAsync();
      } catch {}
    }
  }, [readyToRender]);

  if (!readyToRender) {
    // Keep splash visible; render nothing until fonts are loaded or timeout fires
    return null;
  }

  return <>{children}</>;
}; 